import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons/Button';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Logo } from '~/ui/logo/Logo';
import { TrainingState, useTrainingStore } from '../state';
import { UpdateProfileMutationVariables } from '~/data';
import { updateProfileData } from '~/training/state/settings/updateProfileData';
import { useTrainingParams } from '~/training/useTrainingParams';
import { useRouteMatch } from 'react-router';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import ReactSelect, {
  MultiValue,
  SingleValue,
  StylesConfig
} from 'react-select';
import { shouldSendInteractions, useLmsAdapter } from '~/lms';

type SelectOption = { value: string; label: string };

/** Visually same as the old <small>, but a real button for keyboard/SR. */
const SimpleTooltip = styled.button`
  background-color: #fcf8e9;
  color: #000;
  border-radius: 20px;
  padding: 0px 5px 0px 5px;
  cursor: help;
  position: absolute;
  margin-left: 5px;
  border: none;
  font-size: smaller;
  font-family: inherit;
  line-height: normal;

  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }
`;

const PromptWrapper = styled.div`
  /* No own scroll: with contentScroll="page" the LlamaModal overlay is the
     single scrollport and the card grows to its content (CAT-1609). */
  .flex-block-for-model {
    width: 100%;
  }
  .modalStyleForDarkTheme {
    height: fit-content;
  }
  .click_continue {
    max-width: 340px;
    text-align: center;
  }

  /* Short viewports (LMS iframes): the decorative llama + bubble tail overlap
     the fields and push the overlay into a second scroll (CAT-1609). */
  @media (max-height: 600px) {
    .LlamaModalLlama,
    .pointerStyleForDarkTheme {
      display: none;
    }
    .LlamaModalWrapper {
      padding: 0;
      margin-left: 0;
    }
  }
`;

const ROLE_TOOLTIP_WIDTH = 260;
const VIEWPORT_GAP = 8;
/** Space between the "?" button and the tooltip's side arrow. */
const ARROW_GAP = 14;
/** Distance from the tooltip's top edge to the centre of its side arrow. */
const ARROW_CENTER_Y = 26;

type RoleTooltipPosition = {
  left: number;
  top: number;
  width: number;
  placement: 'left' | 'none';
};

/**
 * The role hint lives in a body portal (position: fixed) so the modal
 * scrollport cannot clip it (CAT-1609). Prefer the right of the "?" button;
 * when the (iframe) viewport is too narrow, drop below it (or above when
 * there is no room below) and keep it inside the viewport.
 */
const getRoleTooltipWidth = () =>
  Math.min(ROLE_TOOLTIP_WIDTH, window.innerWidth - VIEWPORT_GAP * 2);

const getRoleTooltipPosition = (
  anchor: DOMRect,
  tooltipHeight: number
): RoleTooltipPosition => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = getRoleTooltipWidth();
  const besideLeft = anchor.right + ARROW_GAP;

  if (besideLeft + width <= vw - VIEWPORT_GAP) {
    return {
      placement: 'left',
      width,
      left: besideLeft,
      top: Math.max(
        VIEWPORT_GAP,
        Math.min(
          anchor.top + anchor.height / 2 - ARROW_CENTER_Y,
          vh - VIEWPORT_GAP - tooltipHeight
        )
      )
    };
  }

  const left = Math.min(
    Math.max(VIEWPORT_GAP, anchor.left + anchor.width / 2 - width / 2),
    vw - width - VIEWPORT_GAP
  );
  const below = anchor.bottom + VIEWPORT_GAP;
  const top =
    below + tooltipHeight <= vh - VIEWPORT_GAP
      ? below
      : Math.max(VIEWPORT_GAP, anchor.top - VIEWPORT_GAP - tooltipHeight);

  return { placement: 'none', width, left, top };
};

const FieldLabel = styled.label`
  font: inherit;
  color: inherit;
  cursor: default;
`;

const focusedControl = {
  backgroundColor: '#FFF',
  color: '#000'
} as const;

/**
 * Menus render in a body portal with fixed positioning so they are not clipped
 * by the modal scrollport and placement is measured against the (iframe)
 * viewport (CAT-1609). Do not set menuList maxHeight in styles — it would
 * override the height react-select computes for the available space.
 *
 * With fixed positioning react-select renders the full-height menu above the
 * control (off-screen) when neither side has minMenuHeight of room, so both
 * limits scale down on very short viewports (iframes, 400% zoom).
 */
const getMenuPortalProps = (viewportHeight: number) =>
  ({
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    menuPlacement: 'auto',
    maxMenuHeight: Math.min(200, Math.round(viewportHeight * 0.45)),
    minMenuHeight: Math.min(140, Math.round(viewportHeight * 0.25))
  }) as const;

const useViewportHeight = () => {
  const [height, setHeight] = useState(() => window.innerHeight);

  useEffect(() => {
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return height;
};

const singleSelectStyles = (): StylesConfig<SelectOption, false> => ({
  control: (base, state) => ({
    ...base,
    ...focusedControl,
    boxShadow: state.isFocused ? '0 0 0 2px var(--color-focus-ring)' : 'none',
    borderColor: state.isFocused ? 'var(--color-focus-ring)' : base.borderColor,
    outline: 'none'
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#FFF',
    color: '#000',
    zIndex: 5
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f0f0f0' : '#FFF',
    color: '#000'
  })
});

const multiSelectStyles = (): StylesConfig<SelectOption, true> => ({
  control: (base, state) => ({
    ...base,
    ...focusedControl,
    maxWidth: '340px',
    boxShadow: state.isFocused ? '0 0 0 2px var(--color-focus-ring)' : 'none',
    borderColor: state.isFocused ? 'var(--color-focus-ring)' : base.borderColor,
    outline: 'none'
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#FFF',
    color: '#000',
    zIndex: 9999,
    marginBottom: '8px'
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f0f0f0' : '#FFF',
    color: '#000'
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: 'var(--color-accent-dk)',
    color: 'var(--color-on-accent)',
    borderRadius: '10px',
    border: '1px solid var(--color-accent-edge)'
  }),
  multiValueLabel: base => ({
    ...base,
    color: 'var(--color-on-accent)'
  }),
  multiValueRemove: base => ({
    ...base,
    color: 'var(--color-on-accent)',
    ':hover': {
      backgroundColor: 'transparent',
      color: 'var(--color-on-accent)'
    }
  })
});

const selector = (s: TrainingState) =>
  select(
    s,
    'firstName',
    'companyName',
    'logoUrl',
    'title',
    'promptOptions',
    'promptProfile'
  );

export const PromptProfile = () => {
  const { learnerTrainingId } = useTrainingParams();
  const { companyName, logoUrl, promptOptions, promptProfile } =
    useTrainingStore(selector);
  const { url } = useRouteMatch();
  const lms = useLmsAdapter();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = 'prompt-profile-title';
  const roleInputId = 'prompt-profile-role';
  const industryInputId = 'prompt-profile-industry';
  const regionInputId = 'prompt-profile-region';
  const subordinateInputId = 'prompt-profile-subordinate';
  const roleHelpId = 'prompt-profile-role-help';
  const introTextId = 'prompt-profile-intro';
  const confirmTextId = 'prompt-profile-confirm';

  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');
  const [subordinateRegions, setSubordinateRegions] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState(false);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] =
    useState<RoleTooltipPosition | null>(null);
  const roleHelpButtonRef = useRef<HTMLButtonElement>(null);
  const roleTooltipRef = useRef<HTMLSpanElement>(null);
  const menuPortalProps = getMenuPortalProps(useViewportHeight());

  useLayoutEffect(() => {
    if (!showTooltip) {
      setTooltipPosition(null);
      return;
    }

    const update = () => {
      const anchor = roleHelpButtonRef.current;
      if (!anchor) return;
      const tooltip = roleTooltipRef.current;
      // Height depends on width (text wraps), so apply the final width before measuring.
      if (tooltip) tooltip.style.width = `${getRoleTooltipWidth()}px`;
      setTooltipPosition(
        getRoleTooltipPosition(
          anchor.getBoundingClientRect(),
          tooltip?.offsetHeight ?? 0
        )
      );
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showTooltip]);

  const getValueForPromptOption = (id: string | number, optionType: string) => {
    const optionMap: { [key: string]: [number, string][] } = {
      industries: promptOptions?.industries || [],
      regions: promptOptions?.regions || [],
      subordinate_regions: promptOptions?.subordinate_regions || []
    };

    const options = optionMap[optionType];
    if (!options) {
      throw new Error(`Invalid option type: ${optionType}`);
    }

    const option = options.find(el => el[0] == id);
    return option ? option[1] : '';
  };

  useEffect(() => {
    if (!promptProfile && window && window.location) {
      window.location.href = `${url}`
        .replace('//', '/')
        .replace('form', 'onboarding');
    }
  }, [promptProfile, url]);

  useEffect(() => {
    if (
      !promptOptions ||
      !promptOptions.roles ||
      promptOptions.roles.length == 0
    ) {
      setDisableSaveBtn(false);
    }
  }, [promptOptions]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      if (showTooltip) {
        setShowTooltip(false);
        return;
      }

      if (confirmation) {
        setConfirmation(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmation, showTooltip]);

  // Focus step copy so SR announces the current screen (form intro or confirm).
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(confirmation ? confirmTextId : introTextId)
        ?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [confirmation]);

  const showConfirmation = () => {
    setConfirmation(true);
  };

  const SendCmiIntegration = (
    role: string,
    industry: string,
    region: string,
    subordinateRegions: string[] | undefined
  ) => {
    if (!shouldSendInteractions() || !lms.isAvailable) {
      return;
    }

    try {
      if (role) {
        lms.recordMultipleChoiceInteraction(
          'profile-role',
          role,
          true,
          role,
          'Role: '
        );
      }

      if (industry) {
        lms.recordMultipleChoiceInteraction(
          'profile-industry',
          industry,
          true,
          industry,
          `Industry: `
        );
      }

      if (region) {
        lms.recordMultipleChoiceInteraction(
          'profile-region',
          region,
          true,
          region,
          `Location: `
        );
      }

      if (
        role === 'supervisor' &&
        subordinateRegions &&
        subordinateRegions.length > 0
      ) {
        subordinateRegions.forEach((subRegion, index) => {
          lms.recordMultipleChoiceInteraction(
            `profile-subordinate-region-${index}`,
            subRegion,
            true,
            subRegion,
            `Direct Reports' Regions: `
          );
        });
      }
    } catch (error) {
      console.error('Error in SendCmiIntegration:', error);
    }
  };

  const sendData = async () => {
    SendCmiIntegration(
      role,
      getValueForPromptOption(industry, 'industries'),
      getValueForPromptOption(region, 'regions'),
      subordinateRegions.map(region =>
        getValueForPromptOption(region, 'subordinate_regions')
      )
    );

    const dataForSending = {
      learnerTrainingUuid: learnerTrainingId,
      role: role,
      industryId: industry ? Number(industry) : undefined,
      regionId: region ? Number(region) : undefined,
      subordinateRegionIds:
        role == 'supervisor'
          ? subordinateRegions && subordinateRegions.length > 0
            ? subordinateRegions.map(Number)
            : undefined
          : undefined
    } as UpdateProfileMutationVariables;

    const learnerTraining = await updateProfileData(dataForSending);

    if (learnerTraining && window && window.location) {
      window.location.href = `${url}`
        .replace('//', '/')
        .replace(/\/t\/[^/]+\/form\/?$/, `/t/${learnerTraining.id}/onboarding`);
    }
  };

  const subordinateSummary =
    subordinateRegions.length > 0
      ? subordinateRegions
          .map(id => getValueForPromptOption(id, 'subordinate_regions'))
          .join(', ')
      : '';

  return (
    <PromptWrapper>
      <LlamaModal
        horizontal="center"
        vertical="center"
        allowScroll
        contentScroll="page"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <Flex
            w="md"
            minHeight="md"
            flexDirection="column"
            p="xl"
            alignItems="center"
            justifyContent="space-between"
            className="flex-block-for-model"
          >
            <x.div className="logoDark">
              <Logo src={logoUrl} height={36} />
              {!logoUrl && (
                <p style={{ paddingBottom: '20px' }}>{companyName}</p>
              )}
            </x.div>
            <x.h2 id={titleId} fontFamily="llama">
              {t`Welcome!`}
            </x.h2>
            {confirmation ? (
              <div
                id={confirmTextId}
                className="click_continue"
                tabIndex={-1}
                style={{ outline: 'none' }}
              >
                <p>{t`By clicking on "Continue", I certify that the information below is correct and that I will have to retake the course if it is incorrect.`}</p>
                {role && (
                  <div>
                    {t`Role`}: {role}
                  </div>
                )}
                {industry && (
                  <div>
                    {t`Industry`}:{' '}
                    {getValueForPromptOption(industry, 'industries')}
                  </div>
                )}
                {region && (
                  <div>
                    {t`Region`}: {getValueForPromptOption(region, 'regions')}
                  </div>
                )}
                {role === 'supervisor' &&
                  subordinateRegions &&
                  subordinateRegions.length > 0 && (
                    <div>
                      {t`Subordinate Regions`}: {subordinateSummary}
                    </div>
                  )}
              </div>
            ) : (
              <div>
                <p id={introTextId} tabIndex={-1} style={{ outline: 'none' }}>
                  {t`Please answer a few questions about yourself:`}
                </p>
                <div style={{ marginTop: '12px', maxWidth: '340px' }}>
                  <x.h3>
                    <FieldLabel htmlFor={roleInputId}>
                      {t`Select Your Role`}{' '}
                    </FieldLabel>
                    {showTooltip &&
                      createPortal(
                        <span
                          ref={roleTooltipRef}
                          id={roleHelpId}
                          role="tooltip"
                          style={{
                            position: 'fixed',
                            display: 'block',
                            zIndex: 10000,
                            left: tooltipPosition?.left ?? 0,
                            top: tooltipPosition?.top ?? 0,
                            width: tooltipPosition?.width ?? ROLE_TOOLTIP_WIDTH,
                            visibility: tooltipPosition ? 'visible' : 'hidden'
                          }}
                        >
                          <Tooltip
                            placement={tooltipPosition?.placement ?? 'left'}
                            style={{
                              position: 'relative',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          >{t`You are a supervisor if you manage people and have direct reports.`}</Tooltip>
                        </span>,
                        document.body
                      )}
                    <SimpleTooltip
                      ref={roleHelpButtonRef}
                      type="button"
                      aria-label={t`More information about roles`}
                      aria-expanded={showTooltip}
                      aria-controls={showTooltip ? roleHelpId : undefined}
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                      onFocus={() => setShowTooltip(true)}
                      onBlur={() => setShowTooltip(false)}
                    >
                      ?
                    </SimpleTooltip>
                  </x.h3>
                  <ReactSelect<SelectOption, false>
                    inputId={roleInputId}
                    name="role"
                    options={(promptOptions?.roles || []).map((l: string) => ({
                      value: l,
                      label: l
                    }))}
                    onChange={(selectedOption: SingleValue<SelectOption>) => {
                      setRole(selectedOption ? selectedOption.value : '');
                      setDisableSaveBtn(false);
                    }}
                    value={role ? { value: role, label: role } : null}
                    placeholder={t`Select`}
                    {...menuPortalProps}
                    styles={singleSelectStyles()}
                  />
                </div>
                {promptOptions?.industries && (
                  <div style={{ marginTop: '12px' }}>
                    <x.h3>
                      <FieldLabel htmlFor={industryInputId}>
                        {t`Industry`}
                      </FieldLabel>
                    </x.h3>
                    <ReactSelect<SelectOption, false>
                      inputId={industryInputId}
                      name="industry"
                      options={Object.values(promptOptions?.industries).map(
                        (l: [number, string]) => ({
                          value: String(l[0]),
                          label: l[1]
                        })
                      )}
                      onChange={(selectedOption: SingleValue<SelectOption>) => {
                        setIndustry(selectedOption ? selectedOption.value : '');
                      }}
                      value={
                        industry
                          ? {
                              value: industry,
                              label: getValueForPromptOption(
                                industry,
                                'industries'
                              )
                            }
                          : null
                      }
                      placeholder={t`Select an option`}
                      {...menuPortalProps}
                      styles={singleSelectStyles()}
                    />
                  </div>
                )}
                {promptOptions?.regions && (
                  <div style={{ marginTop: '12px' }}>
                    <x.h3>
                      <FieldLabel htmlFor={regionInputId}>
                        {t`Select Your Location`}
                      </FieldLabel>
                    </x.h3>
                    <ReactSelect<SelectOption, false>
                      inputId={regionInputId}
                      name="region"
                      options={Object.values(promptOptions?.regions).map(
                        (l: [number, string]) => ({
                          value: String(l[0]),
                          label: l[1]
                        })
                      )}
                      onChange={(selectedOption: SingleValue<SelectOption>) => {
                        setRegion(selectedOption ? selectedOption.value : '');
                      }}
                      value={
                        region
                          ? {
                              value: region,
                              label: getValueForPromptOption(region, 'regions')
                            }
                          : null
                      }
                      placeholder={t`Select an option`}
                      {...menuPortalProps}
                      styles={singleSelectStyles()}
                    />
                  </div>
                )}
                {role === 'supervisor' &&
                  promptOptions?.subordinate_regions &&
                  promptOptions?.subordinate_regions.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <x.h3>
                        <FieldLabel htmlFor={subordinateInputId}>
                          {t`Select Your Direct Reports’ Regions`}
                        </FieldLabel>
                      </x.h3>
                      <ReactSelect<SelectOption, true>
                        inputId={subordinateInputId}
                        name="subordinateRegions"
                        options={promptOptions?.subordinate_regions.map(
                          (region: [number, string]) => ({
                            value: String(region[0]),
                            label: region[1]
                          })
                        )}
                        isMulti
                        {...menuPortalProps}
                        onChange={(
                          selectedOptions: MultiValue<SelectOption>
                        ) => {
                          setSubordinateRegions(
                            selectedOptions
                              ? selectedOptions.map(option => option.value)
                              : []
                          );
                        }}
                        placeholder={t`Select an option`}
                        value={subordinateRegions.map(region => ({
                          value: region,
                          label: getValueForPromptOption(
                            region,
                            'subordinate_regions'
                          )
                        }))}
                        aria-label={
                          subordinateSummary
                            ? t`Select Your Direct Reports’ Regions. Selected: ${subordinateSummary}`
                            : t`Select Your Direct Reports’ Regions`
                        }
                        styles={multiSelectStyles()}
                      />
                      <span className="sr-only" aria-live="polite">
                        {subordinateSummary
                          ? t`Selected regions: ${subordinateSummary}`
                          : ''}
                      </span>
                    </div>
                  )}
              </div>
            )}
            {confirmation ? (
              <div>
                <Button
                  style={{ marginRight: '20px' }}
                  mt={'20px'}
                  onClick={() => setConfirmation(false)}
                >{t`Back`}</Button>
                <Button mt={'20px'} onClick={sendData}>{t`Continue`}</Button>
              </div>
            ) : (
              <Button
                mt={'20px'}
                onClick={showConfirmation}
                disabled={disableSaveBtn}
                aria-disabled={disableSaveBtn}
              >{t`Save`}</Button>
            )}
          </Flex>
        </div>
      </LlamaModal>
    </PromptWrapper>
  );
};
